import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  makeStyles,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Collapse,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@material-ui/icons';
import { ToggleButton, ToggleButtonGroup, Autocomplete } from '@material-ui/lab';
import axios from 'axios';
import { useApiBaseUrl } from '../../common/CommonMethods/CommonMethods';
import { appUrl } from '../../../../../utils/config';

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      maxWidth: 800,
      width: '100%',
      overflowX: 'hidden',
      overflowY: 'auto',
    },
  },
  modalHeader: {
    padding: '20px 28px 16px',
    borderBottom: '1px solid #e5e5e5',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  titleText: {
    fontWeight: 600,
    fontSize: 20,
    color: '#1A1A1A',
  },
  subtitle: {
    marginTop: 6,
    fontSize: 13,
    color: '#6B7280',
    lineHeight: 1.4,
  },
  toggleGroup: {
    margin: '0 0 24px',
    display: 'inline-flex',
    background: '#F1F5F9',
    borderRadius: 8,
    padding: 3,
    gap: 2,
  },
  toggleButton: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: 13,
    padding: '7px 16px',
    border: 'none',
    borderRadius: 6,
    color: '#6B7280',
    '&.Mui-selected': {
      background: 'linear-gradient(135deg, #3B173F, #073270)',
      color: '#fff',
      '&:hover': {
        background: 'linear-gradient(135deg, #3B173F, #073270)',
      },
    },
  },
  dialogContent: {
    padding: '20px 28px',
    overflowX: 'hidden',
    overflowY: 'auto',
    maxHeight: '60vh',
  },
  dialogActions: {
    padding: '16px 28px',
    borderTop: '1px solid #e5e5e5',
    justifyContent: 'space-between',
    background: '#F7F7F8',
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1A1A1A',
    marginBottom: 10,
  },
  // PO card with external checkbox
  poRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 24,
  },
  extCheckbox: {
    marginTop: 16,
  },
  poCard: {
    border: '1.5px solid #e5e5e5',
    borderRadius: 10,
    padding: '14px 18px',
    background: '#fff',
    flex: 1,
    transition: 'all 0.2s',
  },
  poCardSelected: {
    borderColor: '#073270',
    background: '#F0F4FF',
  },
  poCardGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '12px 24px',
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 3,
  },
  fieldValue: {
    fontSize: 13,
    color: '#1A1A1A',
    lineHeight: 1.4,
  },
  fieldValueBold: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1A1A1A',
  },
  fieldValueCount: {
    fontSize: 15,
    fontWeight: 700,
    color: '#073270',
  },
  // Scope box
  scopeBox: {
    background: '#F7F7F8',
    border: '1px solid #e5e5e5',
    borderRadius: 10,
    padding: '16px 20px',
  },
  scopeHint: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 12,
  },
  scopeChildren: {
    paddingLeft: 16,
    borderLeft: '2px solid #e5e5e5',
    marginLeft: 9,
  },
  scopeChildrenDisabled: {
    opacity: 0.4,
    pointerEvents: 'none',
  },
  cbLabel: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    cursor: 'pointer',
    padding: '8px 0',
  },
  cbTextLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: '#1A1A1A',
  },
  cbTextDesc: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 1.4,
  },
  countBadge: {
    fontSize: 11,
    color: '#073270',
    fontWeight: 500,
    marginLeft: 6,
  },
  // Picker panel
  pickerPanel: {
    margin: '4px 0 8px 30px',
    padding: '12px 14px',
    background: '#fff',
    border: '1.5px solid #e5e5e5',
    borderRadius: 8,
    overflowX: 'hidden',
  },
  pickerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  pickerTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#1A1A1A',
  },
  selectAllLink: {
    fontSize: 12,
    color: '#073270',
    cursor: 'pointer',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
  },
  pickerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    maxHeight: 200,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  pickItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': { background: '#F7F7F8' },
  },
  pickId: {
    fontSize: 13,
    fontWeight: 700,
    color: '#073270',
    whiteSpace: 'nowrap',
    minWidth: 60,
  },
  pickDesc: {
    flex: 1,
    fontSize: 12,
    color: '#1A1A1A',
    lineHeight: 1.4,
  },
  // Duplicate PO
  totalStrip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    background: '#F0F4FF',
    border: '1px solid #C7D2FE',
    borderRadius: 8,
    marginBottom: 16,
  },
  totalLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: '#073270',
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#073270',
  },
  dupCard: {
    border: '1.5px solid #e5e5e5',
    borderRadius: 10,
    background: '#fff',
    transition: 'all 0.2s',
    overflow: 'hidden',
    marginBottom: 10,
  },
  dupCardSelected: {
    borderColor: '#073270',
    background: '#F0F4FF',
  },
  dupCardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 18px',
    cursor: 'pointer',
  },
  dupCardBody: {
    padding: '0 18px 14px 52px',
  },
  dupItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'background 0.12s',
    border: '1px solid #e5e5e5',
    marginBottom: 4,
    background: '#fff',
    '&:hover': { background: '#F7F7F8' },
  },
  dupItemId: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1A1A1A',
    minWidth: 50,
  },
  dupItemDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
  },
  dupItemSubLabel: {
    fontSize: 9,
    fontWeight: 500,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  dupItemValue: {
    fontSize: 11,
    color: '#1A1A1A',
    fontWeight: 500,
  },
  selectedForDeletion: {
    fontSize: 15,
    fontWeight: 700,
    color: '#EC6535',
  },
  // Danger / confirmation
  dangerBox: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 10,
    padding: '16px 20px',
    marginBottom: 20,
  },
  dangerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  dangerTitleText: {
    fontSize: 15,
    fontWeight: 600,
    color: '#991B1B',
  },
  dangerText: {
    fontSize: 13,
    color: '#991B1B',
    lineHeight: 1.6,
  },
  scopeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '6px 12px',
    borderRadius: 6,
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    fontSize: 13,
    fontWeight: 500,
    color: '#991B1B',
    marginRight: 10,
    marginBottom: 8,
  },
  affectedRow: {
    padding: '12px 16px',
    background: '#F7F7F8',
    borderRadius: 8,
    border: '1px solid #e5e5e5',
    marginBottom: 8,
  },
  affectedPONum: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1A1A1A',
  },
  affectedPODesc: {
    fontSize: 12,
    color: '#6B7280',
    marginLeft: 12,
  },
  detailChip: {
    display: 'inline-flex',
    padding: '3px 8px',
    background: '#F0F4FF',
    border: '1px solid #C7D2FE',
    borderRadius: 4,
    fontSize: 11,
    color: '#073270',
    fontWeight: 500,
    margin: '2px 3px 2px 0',
  },
  detailChipLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 4,
    marginTop: 6,
  },
  // Success
  successOverlay: {
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 450,
    minHeight: 350,
    borderRadius: 8,
  },
  successBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    textAlign: 'center',
    width: '100%',
  },
  successIcon: {
    fontSize: '85px',
    color: '#28a745', // Vibrant green
    marginBottom: 30,
  },
  successOkButton: {
    backgroundColor: '#ec6535', // Match the project's orange
    color: '#fff',
    borderRadius: '25px',
    padding: '8px 60px',
    textTransform: 'none',
    fontSize: '18px',
    fontWeight: 600,
    marginTop: 40,
    '&:hover': {
      backgroundColor: '#d4572e',
    },
  },
  successText: {
    fontSize: 20,
    fontWeight: 600,
    color: '#1A1A1A',
    textAlign: 'center',
  },
  // Buttons
  btnSecondary: {
    border: '1.5px solid #e5e5e5',
    background: '#fff',
    color: '#6B7280',
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: 8,
    padding: '10px 24px',
  },
  btnPrimary: {
    background: '#EC6535',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: 8,
    padding: '10px 24px',
    transition: 'all 0.2s ease',
    '&:hover': { 
      backgroundColor: '#e64a19', // More intense orange
      opacity: 1,
    },
    '&.Mui-disabled': { background: '#D9D9D9', color: '#6B7280' },
  },
  btnDanger: {
    background: '#EC6535',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    padding: '10px 24px',
    transition: 'all 0.2s ease',
    '&:hover': { 
      backgroundColor: '#e64a19', // More intense orange
      opacity: 1,
    },
    '&.Mui-disabled': { background: '#D9D9D9', color: '#6B7280' },
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 16px',
    color: '#6B7280',
    fontStyle: 'italic',
    fontSize: 13,
  },
  autocomplete: {
    maxWidth: 320,
    marginBottom: 24,
    '& .MuiOutlinedInput-root': { borderRadius: 8 },
  },
  // BCM-575: Reason for Deletion
  reasonSectionWrap: {
    marginTop: 20,
  },
  reasonHeading: {
    fontSize: 15,
    fontWeight: 600,
    color: '#1A1A1A',
    marginBottom: 10,
  },
  reasonInfoBox: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    background: '#EEF2FF',
    border: '1px solid #C7D2FE',
    borderRadius: 8,
    padding: '12px 14px',
    marginBottom: 14,
  },
  reasonInfoText: {
    fontSize: 12,
    color: '#1E3A8A',
    lineHeight: 1.5,
  },
  reasonCard: {
    border: '1px solid #e5e5e5',
    borderRadius: 10,
    padding: '16px 18px',
    background: '#fff',
  },
  reasonGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 14,
  },
  reasonFieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1A1A1A',
    marginBottom: 6,
  },
  reasonAsterisk: {
    color: '#DC2626',
    marginLeft: 4,
    fontWeight: 700,
  },
  reasonSelect: {
    width: '100%',
    '& .MuiOutlinedInput-root': { borderRadius: 8 },
  },
  reasonDescField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      background: '#F7F7F8',
    },
  },
  commentField: {
    width: '100%',
    '& .MuiOutlinedInput-root': { borderRadius: 8 },
  },
  charCount: {
    fontSize: 11,
    color: '#6B7280',
    textAlign: 'right',
    marginTop: 4,
  },
  commentHelp: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    color: '#6B7280',
    marginTop: 4,
  },
}));

const MODES = { SINGLE: 'single', DUPLICATE: 'duplicate' };
const STEPS = { SELECT: 1, CONFIRM: 2, SUCCESS: 3 };
const COMMENT_MAX_LENGTH = 500;

const MarkPOForDeleteModal = ({ open, onClose, onSuccess }) => {
  const classes = useStyles();
  const API_BASE_URL = useApiBaseUrl(appUrl);
  const successTimerRef = useRef(null);

  // --- State ---
  const [mode, setMode] = useState(MODES.SINGLE);
  const [step, setStep] = useState(STEPS.SELECT);
  const [submitting, setSubmitting] = useState(false);

  // Single PO
  const [singleLinePOs, setSingleLinePOs] = useState([]);
  const [multiLinePOs, setMultiLinePOs] = useState([]);
  const [poOptionsLoading, setPoOptionsLoading] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [scheduleLines, setScheduleLines] = useState([]);
  const [poDetailsLoading, setPoDetailsLoading] = useState(false);

  // Single PO scope
  const [deletePO, setDeletePO] = useState(false);
  const [deleteScheduleLines, setDeleteScheduleLines] = useState(false);
  const [selectedSLIDs, setSelectedSLIDs] = useState([]);
  const [pickerExpanded, setPickerExpanded] = useState(false);

  // Duplicate PO
  const [duplicatePOs, setDuplicatePOs] = useState([]);
  const [duplicateTotalCount, setDuplicateTotalCount] = useState(0);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [expandedDupPOs, setExpandedDupPOs] = useState([]); // Array to allow multiple expanded cards
  const [dupSelections, setDupSelections] = useState({}); // { poNumber: [schrockenSLIDs] }
  const [dupVisibleCount, setDupVisibleCount] = useState(20);

  // BCM-575: Reason for Deletion
  const [reasonCodes, setReasonCodes] = useState([]);
  const [reasonCodesLoading, setReasonCodesLoading] = useState(false);
  const [selectedReasonCode, setSelectedReasonCode] = useState('');
  const [deletionComment, setDeletionComment] = useState('');

  const handleDupScroll = useCallback((e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 150;
    if (bottom) {
      setDupVisibleCount((prev) => prev + 20);
    }
  }, []);

  // --- Reset ---
  const resetState = useCallback(() => {
    setStep(STEPS.SELECT);
    setSelectedPO(null);
    setScheduleLines([]);
    setDeletePO(false);
    setDeleteScheduleLines(false);
    setSelectedSLIDs([]);
    setPickerExpanded(false);
    setExpandedDupPOs([]);
    setDupSelections({});
    setDupVisibleCount(20);
    setSelectedReasonCode('');
    setDeletionComment('');
  }, []);

  const handleModeChange = useCallback((_, newMode) => {
    if (newMode !== null) {
      setMode(newMode);
      resetState();
    }
  }, [resetState]);

  const handleClose = useCallback(() => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    resetState();
    setMode(MODES.SINGLE);
    onClose();
  }, [onClose, resetState]);

  // --- API Calls ---
  const fetchAllPOs = useCallback(async () => {
    if (!API_BASE_URL) return;
    setPoOptionsLoading(true);
    setDuplicatesLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/fetchUniquePO`);
      if (response.data && response.data.status) {
        const rawPOs = response.data.singlePOs || [];
        const rawDups = response.data.duplicatePOs || [];

        const newSingleLinePOs = [];
        const newMultiLinePOs = [];
        
        // --- Group singlePOs into singleLinePOs and multiLinePOs ---
        const groupedSingle = {};
        rawPOs.forEach(po => {
          if (!groupedSingle[po.scheduleAgreementNumber]) {
            groupedSingle[po.scheduleAgreementNumber] = [];
          }
          groupedSingle[po.scheduleAgreementNumber].push(po);
        });

        Object.entries(groupedSingle).forEach(([poNumber, lines]) => {
          const poObj = {
            scheduleAgreementNumber: poNumber,
            vendorName: lines[0].vendorName,
            vendorNumber: lines[0].vendorNumber,
            vendorMosymphonyId: lines[0].vendorMosymphonyID || lines[0].vendorMosymphonyId,
            receiverLegalName: lines[0].receiverLegalName,
            fgMaterialName: lines[0].fgMaterialName,
            scheduleLineCount: lines.length,
            scheduleLines: lines
          };

          if (lines.length === 1) {
            newSingleLinePOs.push(poObj);
          } else {
            newMultiLinePOs.push(poObj);
          }
        });

        // --- Group duplicatePOs ---
        const newDuplicatePOs = [];
        
        const groupedDups = {};
        rawDups.forEach(po => {
          if (!groupedDups[po.scheduleAgreementNumber]) {
            groupedDups[po.scheduleAgreementNumber] = [];
          }
          groupedDups[po.scheduleAgreementNumber].push(po);
        });

        Object.entries(groupedDups).forEach(([poNumber, lines]) => {
          newDuplicatePOs.push({
            scheduleAgreementNumber: poNumber,
            cmoName: lines[0].vendorName,
            cmoId: lines[0].vendorNumber,
            vendorMosymphonyId: lines[0].vendorMosymphonyId,
            receiverLegalName: lines[0].receiverLegalName,
            duplicateCount: lines.length,
            records: lines.map(l => ({
              ...l,
              scheduleAgreementNumber: poNumber,
              createdTimestamp: l.creationDate || l.createdTimestamp,
              scheduleLineStatusText: l.scheduleLineStatusText || l.status || "Created",
            }))
          });
        });

        setSingleLinePOs(newSingleLinePOs);
        setMultiLinePOs(newMultiLinePOs);
        setDuplicatePOs(newDuplicatePOs);
        setDuplicateTotalCount(response.data.totalCount || rawDups.length);
      }
    } catch (error) {
      console.error('Error fetching POs:', error);
    } finally {
      setPoOptionsLoading(false);
      setDuplicatesLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    if (!open) return;
    fetchAllPOs();
  }, [open, fetchAllPOs]);

  // BCM-575: Fetch reason codes (PO-level) for the Reason for Deletion dropdown
  const fetchReasonCodes = useCallback(async () => {
    if (!API_BASE_URL) return;
    setReasonCodesLoading(true);
    try {
      const requestBody = {
        searchField: 'all',
        searchValue: '',
        reasonCodeGroupType: 'all',
        reasonCodeType: 'all',
        status: 'all',
        collaboratorTypeID: 'all',
      };
      const res = await axios.post(`${API_BASE_URL}/fetchReasonCodesDirect`, requestBody);
      if (res.data?.status) {
        const list = res.data?.data?.reasonCodeList || [];
        setReasonCodes(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Error fetching reason codes:', err);
      setReasonCodes([]);
    } finally {
      setReasonCodesLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    if (!open) return;
    fetchReasonCodes();
  }, [open, fetchReasonCodes]);

  useEffect(() => {
    if (selectedPO) {
      setDeletePO(false);
      setDeleteScheduleLines(false);
      setSelectedSLIDs([]);
      setPickerExpanded(false);
      setScheduleLines(selectedPO.scheduleLines || []);
    } else {
      setScheduleLines([]);
    }
  }, [selectedPO]);

  // Auto-select if PO has only 1 schedule line
  useEffect(() => {
    if (scheduleLines.length === 1) {
      setDeletePO(true);
      setDeleteScheduleLines(false); // don't show picker part yet
      setSelectedSLIDs([scheduleLines[0].schrockenSLID]);
      setPickerExpanded(false);
    }
  }, [scheduleLines]);

  // --- Scope Handlers ---
  const handleDeletePOChange = useCallback((checked) => {
    setDeletePO(checked);
    if (checked) {
      if (scheduleLines.length === 1) {
        setDeleteScheduleLines(false);
        setSelectedSLIDs([scheduleLines[0].schrockenSLID]);
      } else {
        setDeleteScheduleLines(true);
        setSelectedSLIDs(scheduleLines.map(sl => sl.schrockenSLID));
      }
      setPickerExpanded(false);
    } else {
      setDeleteScheduleLines(false);
      setSelectedSLIDs([]);
    }
  }, [scheduleLines]);

  const handleDeleteSLChange = useCallback((checked) => {
    if (scheduleLines.length <= 1) return; // ignore for single line
    setDeleteScheduleLines(checked);
    if (!checked) {
      setSelectedSLIDs([]);
      setPickerExpanded(false);
    } else {
      setPickerExpanded(true);
    }
  }, [scheduleLines]);

  const toggleSLID = useCallback((slid) => {
    setSelectedSLIDs(prev =>
      prev.includes(slid) ? prev.filter(s => s !== slid) : [...prev, slid]
    );
  }, []);

  const toggleAllSLIDs = useCallback(() => {
    if (selectedSLIDs.length === scheduleLines.length) {
      setSelectedSLIDs([]);
    } else {
      setSelectedSLIDs(scheduleLines.map(sl => sl.schrockenSLID));
    }
  }, [selectedSLIDs, scheduleLines]);

  // --- Duplicate Handlers ---
  const toggleDupPO = useCallback((poNumber) => {
    setExpandedDupPOs(prev => 
      prev.includes(poNumber) ? prev.filter(p => p !== poNumber) : [...prev, poNumber]
    );
  }, []);

  const handleGroupSelectChange = useCallback((poNumber, records, checked) => {
    // When checking/unchecking the top-level box for a group:
    // 1. Manage expansion state
    setExpandedDupPOs(prev => {
      if (checked && !prev.includes(poNumber)) return [...prev, poNumber];
      if (!checked && prev.includes(poNumber)) return prev.filter(p => p !== poNumber);
      return prev;
    });

    // 2. If unchecking, clear selections for this group
    if (!checked) {
      setDupSelections(prev => {
        const next = { ...prev };
        delete next[poNumber];
        return next;
      });
    }
    // Note: We DO NOT auto-select all records anymore per user request. 
    // The user will pick items one-by-one inside.
  }, []);

  const toggleDupItem = useCallback((poNumber, slid) => {
    setDupSelections(prev => {
      const arr = prev[poNumber] || [];
      const next = arr.includes(slid) ? arr.filter(s => s !== slid) : [...arr, slid];
      return { ...prev, [poNumber]: next };
    });
  }, []);

  const toggleAllDups = useCallback((poNumber, records) => {
    setDupSelections(prev => {
      const arr = prev[poNumber] || [];
      if (arr.length === records.length) {
        return { ...prev, [poNumber]: [] };
      }
      return { ...prev, [poNumber]: records.map(r => r.schrockenSLID) };
    });
  }, []);

  // --- Derived ---
  const allPOs = [...singleLinePOs, ...multiLinePOs];

  const isReviewEnabled = () => {
    if (mode === MODES.SINGLE) {
      if (!selectedPO || scheduleLines.length === 0) return false;
      if (deletePO) return true;
      if (deleteScheduleLines && selectedSLIDs.length > 0) return true;
      return false;
    }
    return Object.values(dupSelections).some(arr => arr.length > 0);
  };

  // BCM-575: Confirm Delete enabled only if Reason Code OR Comment is provided
  const isConfirmEnabled = () => {
    if (submitting) return false;
    const hasReason = !!selectedReasonCode;
    const hasComment = (deletionComment || '').trim().length > 0;
    return hasReason || hasComment;
  };

  const selectedReasonDescription = (() => {
    if (!selectedReasonCode) return '';
    const found = reasonCodes.find(rc => rc.reasonCode === selectedReasonCode);
    return found ? (found.description || found.reasonDescription || '') : '';
  })();

  // --- Submit ---
  const handleSubmit = useCallback(async () => {
    if (!API_BASE_URL) return;
    setSubmitting(true);
    try {
      let markForDeletePOs = [];
      
      if (mode === MODES.SINGLE) {
        const targetSLIDs = deletePO ? scheduleLines.map(sl => sl.schrockenSLID) : selectedSLIDs;
        targetSLIDs.forEach(slid => {
          markForDeletePOs.push({
            schrockenSLID: slid,
            vendorMosymphonyId: selectedPO.vendorMosymphonyId
          });
        });
      } else {
        Object.entries(dupSelections).forEach(([poNumber, sids]) => {
          if (sids.length > 0) {
            const po = duplicatePOs.find(p => p.scheduleAgreementNumber === poNumber);
            sids.forEach(slid => {
              markForDeletePOs.push({
                schrockenSLID: slid,
                vendorMosymphonyId: po?.vendorMosymphonyId
              });
            });
          }
        });
      }

      const finalPayload = {
        markForDeletePOs,
        // BCM-575: Reason for Deletion (audit log) — only reasonCode + comments
        reasonCode: selectedReasonCode || null,
        comments: (deletionComment || '').trim() || null,
      };

      // Send unified payload mapped to same endpoint
      await axios.post(`${API_BASE_URL}/deletePOFromUI`, finalPayload);
      setStep(STEPS.SUCCESS);
      successTimerRef.current = setTimeout(() => {
        if (onSuccess) onSuccess();
        handleClose();
      }, 2400);
    } catch (error) {
      console.error('Error deleting:', error);
    } finally {
      setSubmitting(false);
    }
  }, [API_BASE_URL, mode, selectedPO, deletePO, scheduleLines, selectedSLIDs, dupSelections, duplicatePOs, onSuccess, handleClose, selectedReasonCode, deletionComment]);

  // ===================== RENDERERS =====================

  // --- Single PO: PO Details Card ---
  const renderPOCard = () => {
    if (poDetailsLoading) {
      return <Box className={classes.loadingBox}><CircularProgress size={28} style={{ color: '#073270' }} /></Box>;
    }
    if (!selectedPO) return null;
    return (
      <Box className={classes.poRow}>
        <Checkbox
          checked
          className={classes.extCheckbox}
          style={{ color: '#073270' }}
          size="small"
          disabled
        />
        <Box className={`${classes.poCard} ${classes.poCardSelected}`}>
          <Box className={classes.poCardGrid}>
            <Box>
              <Typography className={classes.fieldLabel}>PO Number</Typography>
              <Typography className={classes.fieldValueBold}>{selectedPO.scheduleAgreementNumber}</Typography>
            </Box>
            <Box>
              <Typography className={classes.fieldLabel}>CMO</Typography>
              <Typography className={classes.fieldValue}>{selectedPO.vendorName}</Typography>
            </Box>
            <Box>
              <Typography className={classes.fieldLabel}>CMO ID</Typography>
              <Typography className={classes.fieldValue}>{selectedPO.vendorNumber}</Typography>
            </Box>
            <Box>
              <Typography className={classes.fieldLabel}>Number of Schedule Lines</Typography>
              <Typography className={classes.fieldValueCount}>{selectedPO.scheduleLineCount}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // --- Single PO: Deletion Scope ---
  const renderScope = () => {
    if (!selectedPO) return null;
    const slCount = scheduleLines.length;
    const selectedCount = selectedSLIDs.length;
    return (
      <Box className={classes.scopeBox}>
        <Typography className={classes.scopeHint}>
          Selecting "Delete PO" will automatically include Schedule Lines
        </Typography>

        {/* Parent: Delete PO */}
        <Box
          className={classes.cbLabel}
          onClick={() => handleDeletePOChange(!deletePO)}
        >
          <Checkbox
            checked={deletePO}
            style={{ color: deletePO ? '#073270' : undefined, padding: 0 }}
            size="small"
          />
          <Box>
            <Typography className={classes.cbTextLabel}>Delete PO</Typography>
            <Typography className={classes.cbTextDesc}>
              {scheduleLines.length <= 1
                ? 'Only one Schedule Line — entire PO will be deleted'
                : 'Removes the entire Purchase Order record and all child data'}
            </Typography>
          </Box>
        </Box>

        {/* Child: Delete Schedule Lines */}
        <Box className={`${classes.scopeChildren} ${deletePO && slCount > 1 ? classes.scopeChildrenDisabled : ''}`}>
          <Box className={classes.cbLabel} onClick={() => !deletePO && slCount > 1 && handleDeleteSLChange(!deleteScheduleLines)}>
            <Checkbox
              checked={deleteScheduleLines}
              disabled={deletePO || slCount <= 1}
              style={{ color: deleteScheduleLines ? '#073270' : undefined, padding: 0 }}
              size="small"
            />
            <Box>
              <Typography className={classes.cbTextLabel}>
                Delete Schedule Lines
                {!deletePO && slCount > 1 && selectedCount > 0 && (
                  <span className={classes.countBadge}>({selectedCount} of {slCount})</span>
                )}
              </Typography>
              <Typography className={classes.cbTextDesc}>
                {slCount <= 1
                  ? 'Only one Schedule Line available — auto-selected for deletion'
                  : 'Pick which Schedule Lines to delete from the list below'}
              </Typography>
            </Box>
          </Box>

          {/* Picker panel — only show if deleteScheduleLines is active (multi) OR if it's a single SL AND PO is being deleted */}
          <Collapse in={(slCount <= 1 && deletePO) || (deleteScheduleLines && !deletePO)}>
            <Box className={classes.pickerPanel} style={slCount <= 1 ? { opacity: 0.8, pointerEvents: 'none' } : {}}>
              <Box className={classes.pickerHeader}>
                <Typography className={classes.pickerTitle}>Schedule Lines</Typography>
                {slCount > 1 && (
                  <Typography className={classes.selectAllLink} onClick={toggleAllSLIDs}>
                    {selectedSLIDs.length === slCount && slCount > 0 ? 'Deselect All' : 'Select All'}
                  </Typography>
                )}
              </Box>
              <Box className={classes.pickerList}>
                {scheduleLines.length === 0 ? (
                  <Typography className={classes.emptyState}>
                    Select a PO above to see available Schedule Lines
                  </Typography>
                ) : (
                  scheduleLines.map((sl) => {
                    const isChecked = selectedSLIDs.includes(sl.schrockenSLID);
                    return (
                      <Box key={sl.schrockenSLID} className={classes.pickItem} onClick={() => toggleSLID(sl.schrockenSLID)}>
                        <Checkbox
                          checked={isChecked}
                          style={{ color: isChecked ? '#073270' : undefined, padding: 0 }}
                          size="small"
                        />
                        <Typography className={classes.pickId}>{sl.schrockenSLID}</Typography>
                        <Typography className={classes.pickDesc}>
                          Sched. Line {sl.scheduleLineItemNumber} — {sl.fgMaterialName || sl.fgMaterialCode}{sl.targetQuantity ? ` / ${sl.targetQuantity} ${sl.targetQuantityUOM || ''}` : ''}
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>
    );
  };

  // --- Single PO Step 1 ---
  const renderSinglePOStep1 = () => (
    <>
      <Typography className={classes.sectionLabel}>Select PO</Typography>
      <Autocomplete
        className={classes.autocomplete}
        options={allPOs}
        getOptionLabel={(option) => option ? `${option.scheduleAgreementNumber} — ${option.fgMaterialName || option.vendorName}` : ''}
        loading={poOptionsLoading}
        value={selectedPO}
        onChange={(_, newValue) => setSelectedPO(newValue)}
        disablePortal={false}
        ListboxProps={{ style: { maxHeight: 240 } }}
        noOptionsText="No POs found"
        loadingText="Loading POs..."
        renderInput={(params) => (
          <TextField {...params} variant="outlined" placeholder="Search PO Number" size="small" fullWidth />
        )}
        renderOption={(option) => (
          <Box style={{ width: '100%' }}>
            <Typography style={{ fontWeight: 600, fontSize: 14 }}>
              {option.scheduleAgreementNumber} — {option.fgMaterialName || ''}
            </Typography>
          </Box>
        )}
      />
      {renderPOCard()}
      {renderScope()}
    </>
  );

  // --- Duplicate PO Step 1 ---
  const renderDuplicatePOStep1 = () => (
    <>
      {duplicatesLoading ? (
        <Box className={classes.loadingBox}><CircularProgress size={28} style={{ color: '#073270' }} /></Box>
      ) : duplicatePOs.length === 0 ? (
        <Typography className={classes.emptyState}>No duplicate POs found.</Typography>
      ) : (
        <>
          <Box className={classes.totalStrip}>
            <Typography className={classes.totalLabel}>Total Duplicate POs Found</Typography>
            <Typography className={classes.totalValue}>{duplicateTotalCount}</Typography>
          </Box>
          <Typography className={classes.sectionLabel}>Select POs and Choose Which Duplicates to Delete</Typography>
          <Box style={{ maxHeight: 400, overflowY: 'auto', overflowX: 'hidden' }} onScroll={handleDupScroll}>
            {duplicatePOs.slice(0, dupVisibleCount).map((po) => {
              const isExpanded = expandedDupPOs.includes(po.scheduleAgreementNumber);
              const selArr = dupSelections[po.scheduleAgreementNumber] || [];
              const selCount = selArr.length;
              return (
                <Box key={po.scheduleAgreementNumber} className={`${classes.dupCard} ${isExpanded ? classes.dupCardSelected : ''}`}>
                  <Box className={classes.dupCardHeader} onClick={() => toggleDupPO(po.scheduleAgreementNumber)}>
                    <Checkbox
                      checked={isExpanded}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Instead of toggleAll, this now just links the checkbox visual to expansion/selection flow
                        handleGroupSelectChange(po.scheduleAgreementNumber, po.records || [], !isExpanded);
                      }}
                      style={{ color: '#073270', padding: 0, marginTop: 2 }}
                      size="small"
                    />
                    <Box style={{ flex: 1 }}>
                      <Box className={classes.poCardGrid}>
                        <Box>
                          <Typography className={classes.fieldLabel}>PO Number</Typography>
                          <Typography className={classes.fieldValueBold}>{po.scheduleAgreementNumber}</Typography>
                        </Box>
                        <Box>
                          <Typography className={classes.fieldLabel}>CMO</Typography>
                          <Typography className={classes.fieldValue}>{po.cmoName}</Typography>
                        </Box>
                        <Box>
                          <Typography className={classes.fieldLabel}>CMO ID</Typography>
                          <Typography className={classes.fieldValue}>{po.cmoId}</Typography>
                        </Box>
                        <Box>
                          <Typography className={classes.fieldLabel}>Number of Duplicate POs</Typography>
                          <Typography className={classes.fieldValueCount}>{po.duplicateCount}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Collapse in={isExpanded}>
                    <Box className={classes.dupCardBody}>
                      <Box className={classes.pickerHeader} style={{ marginTop: 4 }}>
                        <Typography className={classes.pickerTitle}>
                          Duplicate POs by Collabrix PO ID — select which to delete
                        </Typography>
                        <Typography className={classes.selectAllLink} onClick={(e) => { e.stopPropagation(); toggleAllDups(po.scheduleAgreementNumber, po.records || []); }}>
                          {selCount === (po.records || []).length ? 'Deselect All' : 'Select All'}
                        </Typography>
                      </Box>
                      <Box style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {(po.records || []).map((rec) => {
                          const isChecked = selArr.includes(rec.schrockenSLID);
                          const created = rec.createdTimestamp ? new Date(rec.createdTimestamp).toLocaleString() : '';
                          return (
                            <Box key={rec.schrockenSLID} className={classes.dupItem} onClick={(e) => { e.stopPropagation(); toggleDupItem(po.scheduleAgreementNumber, rec.schrockenSLID); }}>
                              <Checkbox
                                checked={isChecked}
                                style={{ color: isChecked ? '#073270' : undefined, padding: 0 }}
                                size="small"
                              />
                              <Box style={{ minWidth: 60, marginLeft: 4 }}>
                                <Typography className={classes.dupItemSubLabel}>ID</Typography>
                                <Typography className={classes.dupItemId} style={{ minWidth: 'auto' }}>{rec.schrockenSLID}</Typography>
                              </Box>
                              
                              <Box className={classes.dupItemDetail}>
                                <Typography className={classes.dupItemSubLabel}>PO Number</Typography>
                                <Typography className={classes.dupItemValue}>{rec.scheduleAgreementNumber}</Typography>
                              </Box>

                              <Box className={classes.dupItemDetail} style={{ flex: 1.5 }}>
                                <Typography className={classes.dupItemSubLabel}>Created Date</Typography>
                                <Typography className={classes.dupItemValue}>{created || 'N/A'}</Typography>
                              </Box>

                              <Box className={classes.dupItemDetail}>
                                <Typography className={classes.dupItemSubLabel}>Status</Typography>
                                <Typography className={classes.dupItemValue}>{rec.scheduleLineStatusText || 'N/A'}</Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </>
  );

  // --- Confirmation ---
  const renderConfirmation = () => {
    const isSingle = mode === MODES.SINGLE;
    return (
      <>
        <Box className={classes.dangerBox}>
          <Box className={classes.dangerTitle}>
            <WarningIcon style={{ color: '#EC6535', fontSize: 18 }} />
            <Typography className={classes.dangerTitleText}>Review and Confirm Deletion</Typography>
          </Box>
          <Typography className={classes.dangerText}>
            This action cannot be undone. Please review the records below, provide a reason, and confirm to proceed.
          </Typography>
        </Box>

        <Typography className={classes.sectionLabel}>Affected POs</Typography>
        {isSingle && selectedPO && (
          <Box className={classes.affectedRow}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography component="span" className={classes.affectedPONum}>{selectedPO.scheduleAgreementNumber}</Typography>
                <Typography component="span" className={classes.affectedPODesc}>{selectedPO.vendorName}</Typography>
              </Box>
            </Box>
            {!deletePO && selectedSLIDs.length > 0 && (
              <Box>
                <Typography className={classes.detailChipLabel}>Collabrix Schedule Line IDs</Typography>
                {selectedSLIDs.map(id => <span key={id} className={classes.detailChip}>{id}</span>)}
              </Box>
            )}
          </Box>
        )}
        {!isSingle && Object.entries(dupSelections).filter(([, a]) => a.length > 0).map(([poNum, sids]) => {
          const po = duplicatePOs.find(p => p.scheduleAgreementNumber === poNum);
          return (
            <Box key={poNum} className={classes.affectedRow}>
              <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography component="span" className={classes.affectedPONum}>{poNum}</Typography>
                  <Typography component="span" className={classes.affectedPODesc}>{po?.cmoName}</Typography>
                </Box>
                <Typography style={{ fontSize: 12, fontWeight: 600, color: '#EC6535' }}>
                  {sids.length} of {po?.duplicateCount} duplicates
                </Typography>
              </Box>
              <Box>
                <Typography className={classes.detailChipLabel}>Collabrix PO IDs to Delete</Typography>
                {sids.map(id => <span key={id} className={classes.detailChip}>{id}</span>)}
              </Box>
            </Box>
          );
        })}

        {renderReasonForDeletion()}
      </>
    );
  };

  // --- BCM-575: Reason for Deletion ---
  const renderReasonForDeletion = () => {
    const commentRequired = !selectedReasonCode;
    const commentLength = (deletionComment || '').length;

    return (
      <Box className={classes.reasonSectionWrap}>
        <Typography className={classes.reasonHeading}>Reason for Deletion</Typography>

        <Box className={classes.reasonInfoBox}>
          <InfoOutlinedIcon style={{ color: '#1E3A8A', fontSize: 18, marginTop: 1 }} />
          <Typography className={classes.reasonInfoText}>
            Either select a <strong>Reason Code</strong> from the list, or provide a{' '}
            <strong>Comment</strong> explaining the deletion. At least one is required for audit
            purposes.
          </Typography>
        </Box>

        <Box className={classes.reasonCard}>
          <Box style={{ marginBottom: 14 }}>
            <Typography className={classes.reasonFieldLabel}>Reason Code / Description</Typography>
            <FormControl variant="outlined" size="small" className={classes.reasonSelect} fullWidth>
              <Select
                value={selectedReasonCode}
                onChange={(e) => setSelectedReasonCode(e.target.value)}
                displayEmpty
                disabled={reasonCodesLoading || submitting}
                renderValue={(val) => {
                  if (!val) {
                    return (
                      <span style={{ color: '#9CA3AF' }}>
                        -- Select a reason code --
                      </span>
                    );
                  }
                  const found = reasonCodes.find((rc) => rc.reasonCode === val);
                  const desc = found ? (found.description || found.reasonDescription || '') : '';
                  return desc ? `${val} — ${desc}` : val;
                }}
              >
                <MenuItem value="">
                  <em>-- Select a reason code --</em>
                </MenuItem>
                {reasonCodes.map((rc) => (
                  <MenuItem key={rc.reasonCode} value={rc.reasonCode}>
                    {rc.reasonCode} — {rc.description || rc.reasonDescription || ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography className={classes.reasonFieldLabel}>
              Comment
              {commentRequired && <span className={classes.reasonAsterisk}>*</span>}
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              multiline
              minRows={3}
              maxRows={5}
              className={classes.commentField}
              value={deletionComment}
              onChange={(e) => {
                const next = e.target.value || '';
                if (next.length <= COMMENT_MAX_LENGTH) setDeletionComment(next);
              }}
              placeholder={
                commentRequired
                  ? 'Comment is required when no reason code is selected'
                  : 'Add additional context (optional) for the deletion being performed.'
              }
              disabled={submitting}
              inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
            />
            <Typography className={classes.charCount}>
              {commentLength} / {COMMENT_MAX_LENGTH}
            </Typography>
            {commentLength > 0 && !selectedReasonCode && (
              <Typography className={classes.commentHelp}>
                <InfoOutlinedIcon style={{ fontSize: 13 }} />
                Comment provided. You may also select a reason code.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // --- Success ---
  const renderSuccess = () => {
    let title = "Items Deleted Successfully";
    if (mode === MODES.SINGLE && selectedPO) {
      if (deletePO) {
        title = `PO Number ${selectedPO.scheduleAgreementNumber} marked\nfor deletion successfully`;
      } else {
        title = "Selected Schedule Lines\nDeleted Successfully";
      }
    } else if (mode === MODES.DUPLICATE) {
      title = "Selected Duplicate Records\nDeleted Successfully";
    }

    return (
      <Box className={classes.successOverlay}>
        <Box className={classes.successBox}>
          <CheckCircleIcon className={classes.successIcon} />
          <Typography 
            variant="h5" 
            style={{ fontWeight: 700, color: '#000', whiteSpace: 'pre-line', lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          
          <Button 
            variant="contained" 
            className={classes.successOkButton}
            onClick={handleClose}
          >
            Ok
          </Button>
        </Box>
      </Box>
    );
  };

  const subtitleText = step === STEPS.CONFIRM
    ? 'Review the deletion scope, provide a reason, and confirm'
    : mode === MODES.SINGLE
      ? 'Select deletion mode and choose which records to remove'
      : 'Select POs and choose which duplicates to delete';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      maxWidth={false}
      disableBackdropClick={submitting || step === STEPS.SUCCESS}
      disableEscapeKeyDown={submitting || step === STEPS.SUCCESS}
    >
      {step === STEPS.SUCCESS ? (
        renderSuccess()
      ) : (
        <>
          <Box className={classes.modalHeader}>
            <Box>
              <Typography className={classes.titleText}>Mark PO for Delete</Typography>
              <Typography className={classes.subtitle}>{subtitleText}</Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} disabled={submitting}>
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent className={classes.dialogContent}>
            {/* Mode Toggle */}
            <Box style={{ marginBottom: 10 }}>
              <Typography className={classes.sectionLabel} style={{ marginBottom: 10 }}>Deletion Mode</Typography>
              <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small" className={classes.toggleGroup}>
                <ToggleButton value={MODES.SINGLE} className={classes.toggleButton}>Single PO</ToggleButton>
                <ToggleButton value={MODES.DUPLICATE} className={classes.toggleButton}>Duplicate POs</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {step === STEPS.SELECT && mode === MODES.SINGLE && renderSinglePOStep1()}
            {step === STEPS.SELECT && mode === MODES.DUPLICATE && renderDuplicatePOStep1()}
            {step === STEPS.CONFIRM && renderConfirmation()}
          </DialogContent>

          <DialogActions className={classes.dialogActions}>
            <Button
              variant="outlined"
              className={classes.btnSecondary}
              onClick={step === STEPS.CONFIRM ? () => setStep(STEPS.SELECT) : handleClose}
              disabled={submitting}
            >
              {step === STEPS.CONFIRM ? 'Back' : 'Cancel'}
            </Button>
            <Box>
              {step === STEPS.SELECT && (
                <Button
                  variant="contained"
                  className={classes.btnPrimary}
                  onClick={() => setStep(STEPS.CONFIRM)}
                  disabled={!isReviewEnabled()}
                  disableElevation
                >
                  Review Deletion
                </Button>
              )}
              {step === STEPS.CONFIRM && (
                <Button
                  variant="contained"
                  className={classes.btnDanger}
                  onClick={handleSubmit}
                  disabled={!isConfirmEnabled()}
                  disableElevation
                  startIcon={submitting ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <DeleteIcon />}
                >
                  {submitting ? 'Deleting...' : 'Confirm Delete'}
                </Button>
              )}
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default MarkPOForDeleteModal;
